import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PetPassedAwayComponent } from 'src/app/modals/pet-passed-away/pet-passed-away.component';
import { User, ProductType } from 'src/app/interfaces/user';
import { PetService } from 'src/app/services/pet/pet.service';
import {
  PetGender,
  PetStatus,
  Pet,
  PetResidenceStatus,
  PetMissingReport,
  PetMissingReportStatus,
} from 'src/app/interfaces/pet';
import { Alert } from 'src/app/interfaces/alert';
import { PetRehomeModalComponent } from 'src/app/modals/pet-rehome-modal/pet-rehome-modal.component';
import { FileDownloadModalComponent } from 'src/app/modals/file-download-modal/file-download-modal.component';
import { PetMissingModalComponent } from 'src/app/modals/pet-missing-modal/pet-missing-modal.component';
import {
  ImageUploaderModalComponent,
  ImageContent,
} from 'src/app/modals/image-uploader-modal/image-uploader-modal.component';
import { UserService } from 'src/app/services/user/user.service';
import { PetFoundModalComponent } from 'src/app/modals/pet-found-modal/pet-found-modal.component';
import { ArchivePetModalComponent } from 'src/app/modals/archive-pet-modal/archive-pet-modal.component';
import { QR_CODE_PREFERENCES_ROUTE, QR_CODE_ROUTE } from 'src/app/constants/route.constants';
import { QrCodePreferences } from 'src/app/interfaces/qr-code';
import { CloudinaryService } from 'src/app/services/cloudinary/cloudinary.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { isPlatformBrowser } from '@angular/common';
import { ClickTrackerService } from 'src/app/services/click-tracker/click-tracker.service';
import { ClickEventNumbersEnum, TrackingPayloadType } from 'src/app/interfaces/clickEventNumbers';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-my-pet',
  templateUrl: './my-pet.component.html',
  styleUrls: ['./my-pet.component.scss'],
})
export class MyPetComponent implements OnInit, OnDestroy {
  user: User;
  pet: Pet;

  archivePetModalRef?: NgbModalRef;
  rehomeModalRef?: NgbModalRef;
  petMissingModalRef?: NgbModalRef;
  petFoundModalRef?: NgbModalRef;
  fileModalRef?: NgbModalRef;
  imageUploaderRef?: NgbModalRef;
  newTagModalRef?: NgbModalRef;
  productType = ProductType;
  petGender = PetGender;
  petStatus = PetStatus;
  petResidenceStatus = PetResidenceStatus;
  alert: Alert = null;
  loading = false;
  tagHasBeenOrdered = false;
  petImage: string;
  activeMissingReport: PetMissingReport;
  rejectedMissingReport: PetMissingReport;
  petMissingReportStatus = PetMissingReportStatus;
  qrCodePreferences: QrCodePreferences;
  currentMembershipName: string = 'identibase';
  userAlreadyHasASubscription: boolean = false;
  userHasBasicAccount: boolean = true;
  isBrowser: boolean;
  trackingPayload: TrackingPayloadType = {
    event: ClickEventNumbersEnum.PetDetails,
    details: ''
  };
  companyPhoneNumber: string;
  petNameTitleSuffix: string;
  petTypeText: string;
  petBreedText: string;
  petDateOfBirthText: string;
  petColoursMarkingsText: string;
  petConditionsText: string;
  chipNumberText: string;
  useRebrandedLayout: boolean;

  constructor(
    public userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private petService: PetService,
    private http: HttpClient,
    private cloudinaryService: CloudinaryService,
    public membershipOptionsService: MemberShipOptionsService,
    private clickTrackerService: ClickTrackerService,
    private featureFlagService: FeatureFlagsService,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.pet = this.route.snapshot.data.pet as Pet;
    this.qrCodePreferences = this.route.snapshot.data.qrCodePreferences as QrCodePreferences;

    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.petNameTitleSuffix = this.configService.getTextFromKey('pet-name-title-suffix');
    this.petTypeText = this.configService.getTextFromKey('pet-type-text');
    this.petBreedText = this.configService.getTextFromKey('pet-breed-text');
    this.petDateOfBirthText = this.configService.getTextFromKey('pet-date-of-birth-text');
    this.petColoursMarkingsText = this.configService.getTextFromKey('pet-colours-markings-text');
    this.petConditionsText = this.configService.getTextFromKey('pet-conditions-text');
    this.chipNumberText = this.configService.getTextFromKey('chip-number-text');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');

    this.userAlreadyHasASubscription = this.membershipOptionsService.doesUserHaveMembership(
      this.user
    );

    this.userHasBasicAccount = this.userService.isUserBasic(this.user.productType);

    if (this.userAlreadyHasASubscription) {
      this.currentMembershipName = this.membershipOptionsService.getUserMembershipName(this.user);
    }
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  onClickArchive() {
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.archivePetModalRef = this.modalService.open(ArchivePetModalComponent, modalOptions);
    this.archivePetModalRef.componentInstance.petName = this.pet.name;
    this.archivePetModalRef.result
      .then(() => {
        this.alert = {
          type: 'info',
          message: 'Updating your pet information...',
        };
        this.petService.archivePet(this.pet.id).subscribe(
          () => {
            this.pet.archived = true;
            this.alert = {
              type: 'success',
              message: 'Your pet information has been updated.',
            };
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        );
      })
      .catch(() => {});
  }

  public getQrCodePreferencesForPet(
    petId: number
  ): Observable<QrCodePreferences> {
    return this.http.get<any>(
      `${environment.apiUrl}/api/qr-code-preferences/for-pet/${petId}`
    );
  }

  viewQRCodeData() {
    this.router.navigate([QR_CODE_ROUTE, this.pet.id], {
      queryParams: { preview: true },
    });
  }

  editQRCodePreferences() {
    this.router.navigate(['/smart-tag-for-pet', this.pet.id, QR_CODE_PREFERENCES_ROUTE]);
  }

  onClickUnarchive() {
    this.alert = {
      type: 'info',
      message: 'Updating your pet information...',
    };
    this.petService.unarchivePet(this.pet.id).subscribe(
      () => {
        this.pet.archived = false;
        this.alert = {
          type: 'success',
          message: 'Your pet information has been updated.',
        };
      },
      (err) => {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
      }
    );
  }

  redirectToCompletTOK() {
    this.router.navigate(['/tok-payment'], { queryParams: { petName: this.pet.name }});
  }

  onClickEditPetBio() {
    this.trackEvent('petpage::editpet::clicked');
    this.router.navigate([`/my-pet/${this.pet.id}/edit`]);
  }

  onClickPassedAway() {
    this.trackEvent('petpage::deceasedpet::clicked');
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    const isLastPetAlive = this.user.pets.filter(pet => pet.status === 1).length === 1;

    this.archivePetModalRef = this.modalService.open(PetPassedAwayComponent, modalOptions);
    this.archivePetModalRef.componentInstance.petName = this.pet.name;
    this.archivePetModalRef.componentInstance.currentUserPlan = this.currentMembershipName;
    this.archivePetModalRef.componentInstance.userHasBasicAccount = this.userHasBasicAccount;
    this.archivePetModalRef.componentInstance.isLastPetAlive = isLastPetAlive;
    this.archivePetModalRef.result
      .then((isSubscriptionCancellationRequested: boolean) => {
        this.markPetAsDeceasedWithCancellationOption(isSubscriptionCancellationRequested);
      })
      .catch(() => {});
  }

  markPetAsDeceasedWithCancellationOption(isSubscriptionCancellationRequested: boolean) {
    this.alert = {
      type: 'info',
      message: 'Updating your pet information...',
    };
    this.petService.markPetAsDeceased(this.pet.id, isSubscriptionCancellationRequested).subscribe(
      () => {
        this.pet.status = PetStatus.Deceased;
        this.alert = {
          type: 'success',
          message: 'Your pet information has been updated.',
        };
        this.trackEvent('petpage::deceasedpet::confirmed');
      },
      (err) => {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
      }
    );
  }

  onClickMissing() {
    this.trackEvent('petpage::lostpet::clicked');
    this.router.navigate([`/my-pet/${this.pet.id}/report-missing`]);
  }

  onClickRehome() {
    this.trackEvent('petpage::rehome::clicked');
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.rehomeModalRef = this.modalService.open(PetRehomeModalComponent, modalOptions);
    this.rehomeModalRef.componentInstance.petName = this.pet.name;
    this.rehomeModalRef.result
      .then(() => {
        this.trackEvent('petpage::rehome::closed');
      })
      .catch(() => {});
  }

  // TODO: This has to be removed at some point after the Lost Pet Service is fully tested and
  // we're sure we'll offer for Basic users as well
  onClickMarkLostStolen() {
    this.trackEvent('petpage::markmissing::clicked');
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.petMissingModalRef = this.modalService.open(PetMissingModalComponent, modalOptions);
    this.petMissingModalRef.componentInstance.petName = this.pet.name;
    this.petMissingModalRef.result
      .then((residenceStatus) => {
        this.alert = {
          type: 'info',
          message: 'Updating your pet information...',
        };
        this.petService.updateResidenceStatus(this.pet.id, residenceStatus).subscribe(
          () => {
            this.pet.residence = residenceStatus;
            this.alert = {
              type: 'success',
              message: 'Your pet information has been updated.',
            };
            this.trackEvent('petpage::markmissing::confirmed');
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        );
      })
      .catch(() => {});
  }

  onClickMarkFound() {
    this.trackEvent('petpage::markfound::clicked');
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.petFoundModalRef = this.modalService.open(PetFoundModalComponent, modalOptions);
    this.petFoundModalRef.componentInstance.petName = this.pet.name;
    this.petFoundModalRef.result
      .then(() => {
        this.alert = {
          type: 'info',
          message: 'Updating your pet information...',
        };
        this.petService.updateResidenceStatus(this.pet.id, PetResidenceStatus.AtHome).subscribe(
          () => {
            this.pet.residence = PetResidenceStatus.AtHome;
            this.alert = {
              type: 'success',
              message: 'Your pet information has been updated.',
            };
            this.trackEvent('petpage::markfound::confirmed');
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        );
      })
      .catch(() => {});
  }

  downloadCertificate() {
    this.trackEvent('petpage::chipcert::clicked');
    this.alert = {
      type: 'info',
      message: "Retrieving your pet's certificate...",
    };

    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.petService.getCertificate(this.pet.chipNumber).subscribe(
      (certificate) => {
        this.closeAlert();

        this.fileModalRef = this.modalService.open(FileDownloadModalComponent, modalOptions);
        this.fileModalRef.componentInstance.title = `${this.pet.name}'s certificate`;
        this.fileModalRef.componentInstance.content = `${this.pet.name}'s certificate is ready for download.`;
        this.fileModalRef.componentInstance.fileUrl = certificate.response;
        this.fileModalRef.componentInstance.downloadEventName = 'petpage::chipcert::downloaded';
        this.fileModalRef.result.then(() => {}).catch(() => {});
      },
      (err) => {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
      }
    );
  }

  updateImage(pet: Pet, imageContent: ImageContent): string {
    this.closeAlert();
    pet.imageId = imageContent.fileName;
    pet.imageUrl = imageContent.filePath;
    const petImage = this.getPetImageUrl(pet.imageId);
    this.alert = {
      type: 'success',
      message: 'Your pet image has been updated.',
    };
    // Update header cached pet picture without refreshing the page
    const headerPetData = this.userService.user.pets.find((p) => p.id === pet.id);
    if (headerPetData) {
      headerPetData.imageId = pet.imageId;
      headerPetData.imageUrl = pet.imageUrl;
    }
    return petImage;
  }

  openImageUploader(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.imageUploaderRef = this.modalService.open(ImageUploaderModalComponent, modalOptions);
    this.imageUploaderRef.componentInstance.title = `Edit ${this.pet.name}'s image`;
    this.imageUploaderRef.componentInstance.currentImageUrl = this.pet.imageUrl;
    this.imageUploaderRef.componentInstance.currentImageId = this.pet.imageId;
    this.imageUploaderRef.componentInstance.pet = this.pet;
    this.imageUploaderRef.result
      .then((res) => {
        const imageContent = res as ImageContent;
        this.petImage = this.updateImage(this.pet, imageContent);
        this.trackEvent('petpage::image::uploaded');
      })
      .catch(() => {});
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  public getPetImageUrl(petImageId: string): string {
    return this.cloudinaryService.getImageUrl(petImageId, 360, 360, null);
  }

  private getActiveMissingReport(): PetMissingReport {
    return this.pet.missingPetReports.find(
      (report) =>
        report.reportStatus === PetMissingReportStatus.AwaitingModeration ||
        report.reportStatus === PetMissingReportStatus.Open ||
        report.reportStatus === PetMissingReportStatus.AwaitingClosureFound ||
        report.reportStatus === PetMissingReportStatus.AwaitingClosureDeceased
    );
  }

  private getRejectedMissingReport(): PetMissingReport {
    return this.pet.missingPetReports.find(
      (report) => report.reportStatus === PetMissingReportStatus.ClosedRejected
    );
  }

  ngOnInit(): void {
    if (this.pet.name === '[to_be_completed_by_keeper]') {
      this.router.navigate(['/my-pet', this.pet.id, 'confirm']);
    }
    this.petImage = this.getPetImageUrl(this.pet.imageId);
    this.activeMissingReport = this.getActiveMissingReport();
    this.rejectedMissingReport = this.getRejectedMissingReport();
    if (this.isBrowser) {
      this.clickTrackerService.sendPayload(this.trackingPayload);
    }
    this.getQrCodePreferencesForPet(this.pet.id).subscribe((response) => {
      this.tagHasBeenOrdered = response.tagHasBeenOrdered;
    })
  }

  ngOnDestroy(): void {
    this.archivePetModalRef?.dismiss('destroyed');
    this.rehomeModalRef?.dismiss('destroyed');
    this.petMissingModalRef?.dismiss('destroyed');
    this.fileModalRef?.dismiss('destroyed');
    this.imageUploaderRef?.dismiss('destroyed');
    this.petFoundModalRef?.dismiss('destroyed');
  }
}
